/**
 * Utility functions for state-specific icons and mappings
 */

// US State to abbreviation and flag emoji mapping
export const US_STATES = {
  'Alabama': { abbr: 'AL', emoji: '🏛️', color: '#C8102E' },
  'Alaska': { abbr: 'AK', emoji: '🏔️', color: '#002868' },
  'Arizona': { abbr: 'AZ', emoji: '🌵', color: '#C8102E' },
  'Arkansas': { abbr: 'AR', emoji: '💎', color: '#C8102E' },
  'California': { abbr: 'CA', emoji: '🐻', color: '#003594' },
  'Colorado': { abbr: 'CO', emoji: '⛰️', color: '#002868' },
  'Connecticut': { abbr: 'CT', emoji: '⚓', color: '#002868' },
  'Delaware': { abbr: 'DE', emoji: '💙', color: '#002868' },
  'Florida': { abbr: 'FL', emoji: '🌴', color: '#C8102E' },
  'Georgia': { abbr: 'GA', emoji: '🍑', color: '#C8102E' },
  'Hawaii': { abbr: 'HI', emoji: '🌺', color: '#002868' },
  'Idaho': { abbr: 'ID', emoji: '🥔', color: '#002868' },
  'Illinois': { abbr: 'IL', emoji: '🏛️', color: '#002868' },
  'Indiana': { abbr: 'IN', emoji: '🏎️', color: '#002868' },
  'Iowa': { abbr: 'IA', emoji: '🌽', color: '#002868' },
  'Kansas': { abbr: 'KS', emoji: '🌻', color: '#002868' },
  'Kentucky': { abbr: 'KY', emoji: '🐴', color: '#002868' },
  'Louisiana': { abbr: 'LA', emoji: '⚜️', color: '#002868' },
  'Maine': { abbr: 'ME', emoji: '🦞', color: '#002868' },
  'Maryland': { abbr: 'MD', emoji: '🦀', color: '#C8102E' },
  'Massachusetts': { abbr: 'MA', emoji: '⭐', color: '#002868' },
  'Michigan': { abbr: 'MI', emoji: '🚗', color: '#002868' },
  'Minnesota': { abbr: 'MN', emoji: '⭐', color: '#002868' },
  'Mississippi': { abbr: 'MS', emoji: '🎵', color: '#C8102E' },
  'Missouri': { abbr: 'MO', emoji: '🎵', color: '#C8102E' },
  'Montana': { abbr: 'MT', emoji: '⛰️', color: '#002868' },
  'Nebraska': { abbr: 'NE', emoji: '🌽', color: '#002868' },
  'Nevada': { abbr: 'NV', emoji: '🎰', color: '#002868' },
  'New Hampshire': { abbr: 'NH', emoji: '🗿', color: '#002868' },
  'New Jersey': { abbr: 'NJ', emoji: '🏖️', color: '#002868' },
  'New Mexico': { abbr: 'NM', emoji: '☀️', color: '#C8102E' },
  'New York': { abbr: 'NY', emoji: '🗽', color: '#002868' },
  'North Carolina': { abbr: 'NC', emoji: '⭐', color: '#002868' },
  'North Dakota': { abbr: 'ND', emoji: '🌾', color: '#002868' },
  'Ohio': { abbr: 'OH', emoji: '🌰', color: '#C8102E' },
  'Oklahoma': { abbr: 'OK', emoji: '⭐', color: '#C8102E' },
  'Oregon': { abbr: 'OR', emoji: '🌲', color: '#002868' },
  'Pennsylvania': { abbr: 'PA', emoji: '🔔', color: '#002868' },
  'Rhode Island': { abbr: 'RI', emoji: '⚓', color: '#002868' },
  'South Carolina': { abbr: 'SC', emoji: '🌙', color: '#002868' },
  'South Dakota': { abbr: 'SD', emoji: '🗿', color: '#002868' },
  'Tennessee': { abbr: 'TN', emoji: '🎸', color: '#C8102E' },
  'Texas': { abbr: 'TX', emoji: '⭐', color: '#C8102E' },
  'Utah': { abbr: 'UT', emoji: '🏜️', color: '#C8102E' },
  'Vermont': { abbr: 'VT', emoji: '🍁', color: '#002868' },
  'Virginia': { abbr: 'VA', emoji: '⭐', color: '#002868' },
  'Washington': { abbr: 'WA', emoji: '🌲', color: '#002868' },
  'West Virginia': { abbr: 'WV', emoji: '⛰️', color: '#002868' },
  'Wisconsin': { abbr: 'WI', emoji: '🧀', color: '#C8102E' },
  'Wyoming': { abbr: 'WY', emoji: '🦬', color: '#C8102E' }
};

/**
 * Extract state information from destination string
 * @param {string} destination - Trip destination (e.g., "Yellowstone National Park, Wyoming")
 * @returns {object|null} State object with abbr, emoji, and color, or null if not found
 */
export const extractStateFromDestination = (destination) => {
  if (!destination) return null;

  // Convert to string and normalize
  const destString = String(destination)?.trim();

  // Try to find state name in destination string
  for (const [stateName, stateData] of Object.entries(US_STATES)) {
    // Check full state name (case insensitive)
    if (destString?.toLowerCase()?.includes(stateName?.toLowerCase())) {
      return { name: stateName, ...stateData };
    }
    
    // Check state abbreviation
    const abbrPattern = new RegExp(`\\b${stateData.abbr}\\b`, 'i');
    if (abbrPattern?.test(destString)) {
      return { name: stateName, ...stateData };
    }
  }

  // Common park to state mappings for well-known national parks
  const parkStateMap = {
    'yellowstone': 'Wyoming',
    'yosemite': 'California',
    'grand canyon': 'Arizona',
    'zion': 'Utah',
    'acadia': 'Maine',
    'glacier': 'Montana',
    'rocky mountain': 'Colorado',
    'great smoky mountains': 'Tennessee',
    'everglades': 'Florida',
    'olympic': 'Washington',
    'joshua tree': 'California',
    'sequoia': 'California',
    'death valley': 'California',
    'denali': 'Alaska',
    'arches': 'Utah',
    'bryce canyon': 'Utah',
    'canyonlands': 'Utah',
    'capitol reef': 'Utah',
    'crater lake': 'Oregon',
    'grand teton': 'Wyoming',
    'mount rainier': 'Washington',
    'redwood': 'California',
    'shenandoah': 'Virginia',
    'badlands': 'South Dakota',
    'big bend': 'Texas',
    'carlsbad caverns': 'New Mexico',
    'mammoth cave': 'Kentucky',
    'mesa verde': 'Colorado',
    'petrified forest': 'Arizona',
    'saguaro': 'Arizona',
    'wind cave': 'South Dakota',
    // Texas State Parks
    'pedernales falls': 'Texas',
    'guadalupe river': 'Texas',
    'enchanted rock': 'Texas',
    'palo duro canyon': 'Texas',
    'garner': 'Texas',
    'inks lake': 'Texas',
    'lost maples': 'Texas',
    'brazos bend': 'Texas',
    'dinosaur valley': 'Texas',
    'bastrop': 'Texas',
    'mckinney falls': 'Texas',
    'south llano river': 'Texas',
    'colorado bend': 'Texas',
    'seminole canyon': 'Texas',
    'franklin mountains': 'Texas',
    'lake texana': 'Texas',
    'mustang island': 'Texas',
    'goose island': 'Texas',
    'sea rim': 'Texas',
    'galveston island': 'Texas'
  };

  // Check park name mappings
  const lowerDest = destString?.toLowerCase();
  for (const [parkName, stateName] of Object.entries(parkStateMap)) {
    if (lowerDest?.includes(parkName)) {
      return { name: stateName, ...US_STATES?.[stateName] };
    }
  }

  return null;
};

/**
 * Get state icon component props
 * @param {string} destination - Trip destination
 * @returns {object} Icon props with emoji and styling
 */
export const getStateIconProps = (destination) => {
  const stateInfo = extractStateFromDestination(destination);
  
  if (!stateInfo) {
    // Return default if no state found
    return {
      emoji: '📍',
      abbr: '',
      color: 'var(--color-primary)',
      name: 'Location'
    };
  }

  return {
    emoji: stateInfo?.emoji,
    abbr: stateInfo?.abbr,
    color: stateInfo?.color,
    name: stateInfo?.name
  };
};

/**
 * Format state display with emoji and abbreviation
 * @param {string} destination - Trip destination
 * @returns {string} Formatted state display
 */
export const getStateDisplay = (destination) => {
  const stateInfo = extractStateFromDestination(destination);
  
  if (!stateInfo) {
    return '📍';
  }

  return `${stateInfo?.emoji} ${stateInfo?.abbr}`;
};